import { useMemo } from 'react';
import Cookie from 'js-cookie';
import { useSelector } from 'react-redux';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { COOKIE_LANGUAGE_PREFERENCE, DEFAULT_LANGUAGE, LANGUAGES } from '../config/constants';

/**
 * Getting i18n presedence in our app when starting up. The priority is:
 * 1. User account preference
 * 2. Cookies
 * 3. URL
 */
function getLanguagePreference(
  currentPath: string,
  currentLanguage: string,
  currentUserLanguagePreference: string,
  isCustomRoute: boolean,
  isGetCurrentUserDone: boolean,
) {
  // Wait until we know if a user has logged in or not.
  if (!isGetCurrentUserDone) {
    return null;
  }

  // 1. Use user account preference if available.
  if (currentUserLanguagePreference) {
    return currentUserLanguagePreference;
  }

  const agreedCookieCategories = Cookie.get('cookie-agreed-categories');
  const isPreferencesCookieAgreed = agreedCookieCategories?.includes('preferences');
  const langCookie = Cookie.get(COOKIE_LANGUAGE_PREFERENCE);

  // 2. Use cookie preference if available.
  if (isPreferencesCookieAgreed && langCookie && LANGUAGES[langCookie]) {
    return langCookie;
  }

  // 3A. Use URL if route is custom.
  if (isCustomRoute) {
    const languagePrefixes = Object.keys(LANGUAGES);
    const currentLanguagePrefix = languagePrefixes.find((prefix) => currentPath.split('/')[1] === prefix);

    if (currentLanguagePrefix) {
      return currentLanguagePrefix;
    }
  }

  // 3B. Use current language, gotten from Drupal, if other methods are invalid.
  return currentLanguage;
}

export default function useLanguagePreference() {
  const currentPath = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['router', 'location', 'pathname'], ''),
  );

  const currentLanguage = useSelector<ImmutableRootState<string>, string>(
    (state) => state.getIn(['ui', 'language'], DEFAULT_LANGUAGE),
  );

  const currentUserLanguagePreference = useSelector<ImmutableRootState<string>, string | null>(
    (state) => {
      const uid = state.getIn(['login', 'uid'], null);
      return state.getIn(['user', uid, 'preferred_langcode', 0, 'value'], null);
    },
  );

  const isCustomRoute = useSelector<ImmutableRootState<string>, boolean>(
    (state) => {
      const entityType = state.getIn(['entity', 'entityType'], null);
      const entityStatus = state.getIn(['entity', 'status'], null);
      const entityId = state.getIn(['entity', 'entityId'], null);

      return entityType === null && entityStatus === null && !entityId;
    },
  );

  const isGetCurrentUserDone = useSelector<ImmutableRootState<boolean>, boolean>(
    (state) => state.getIn(['login', 'loggedIn'], null) !== null,
  );

  const preferredLanguage = useMemo(() => getLanguagePreference(
    currentPath,
    currentLanguage,
    currentUserLanguagePreference,
    isCustomRoute,
    isGetCurrentUserDone,
  ), [
    currentPath,
    currentLanguage,
    currentUserLanguagePreference,
    isCustomRoute,
    isGetCurrentUserDone,
  ]);

  return {
    preferredLanguage,
    currentLanguage,
    isCustomRoute,
  };
}
